import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { MdOutlineSubdirectoryArrowRight } from 'react-icons/md';
import { generatePath, Link, useParams } from 'react-router-dom';

import { assessmentManagerChildQuestionsRoute, assessmentManagerQuestionsRoute } from 'App/common/routeLookup';
import { getSingleQuestion } from 'App/services/AssessmentQuestionService';
import { stripAllHtml } from 'App/utils';
import assessmentSettingsPropType from '../../types/assessmentSettings';
import convertAssessmentQuestionToView from '../utils/convertAssessmentQuestionToView';

const BreadcrumbsStyle = styled.div`
    margin: 1.5em 0;

    & svg {
        margin-right: 0.4em;
        margin-left: 0.8em;
    }
`;

const AssessmentManagerQuestionBreadcrumbs = ({ assessment }) => {
    const { categoryId, assessmentId, parentQuestionId, parentAnswerId } = useParams();
    const { values, setFieldValue } = useFormikContext();

    const fetchParentQuestionsRecursive = useCallback(
        async (questionId) => {
            try {
                if (!questionId) return;
                const currQuestionValue = values.questions[questionId];
                if (currQuestionValue && !currQuestionValue.version_id) return;
                const newQuestion = await getSingleQuestion(questionId);
                if (!values.questions[newQuestion.question_id]) {
                    setFieldValue(`questions.${newQuestion.question_id}`, convertAssessmentQuestionToView(newQuestion));
                }
                if (newQuestion.parent_question_id) {
                    await fetchParentQuestionsRecursive(newQuestion.parent_question_id);
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        },
        [setFieldValue, values.questions]
    );

    useEffect(() => {
        fetchParentQuestionsRecursive(parentQuestionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when parentQuestionId changes
    }, [parentQuestionId]);

    if (!parentQuestionId) {
        return null;
    }

    const getBreadcrumbElements = (questionId, answerId, elems = []) => {
        const question = Object.values(values.questions).find((q) =>
            [q.question_id, q.official_question_id].includes(questionId)
        );
        if (!question) {
            return elems;
        }
        elems.unshift(
            <Link
                to={generatePath(assessmentManagerChildQuestionsRoute, {
                    categoryId,
                    assessmentId,
                    parentQuestionId: questionId,
                    parentAnswerId: answerId,
                })}
            >
                {stripAllHtml(question.answers[answerId].option)}
            </Link>
        );
        elems.unshift(stripAllHtml(question.question));
        if (question.parent_question_id) {
            return getBreadcrumbElements(question.parent_question_id, question.parent_answer_id, elems);
        }
        return elems;
    };

    const breadcrumbs = getBreadcrumbElements(parentQuestionId, parentAnswerId);

    return (
        <BreadcrumbsStyle>
            <Link to={generatePath(assessmentManagerQuestionsRoute, { categoryId, assessmentId })}>
                {assessment.name}
            </Link>
            {breadcrumbs.map((elem, index) => (
                <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    style={{ marginLeft: index * 30, fontWeight: index === breadcrumbs.length - 1 ? 'bold' : 'normal' }}
                >
                    <MdOutlineSubdirectoryArrowRight />
                    {elem}
                </div>
            ))}
        </BreadcrumbsStyle>
    );
};

AssessmentManagerQuestionBreadcrumbs.propTypes = {
    assessment: assessmentSettingsPropType.isRequired,
};

export default AssessmentManagerQuestionBreadcrumbs;
